import React from 'react';
import { ExternalLink } from 'lucide-react';
import { useLang } from '@/i18n/useLang';
import roBuyer from '@/data/translations/ro/buyer.json';
import enBuyer from '@/data/translations/en/buyer.json';
import deBuyer from '@/data/translations/de/buyer.json';

interface Props {
  ownerCount?: number;
  motValidUntil?: string;
  serviceHistoryComplete?: boolean;
  accidentFree?: boolean;
  mileageKm?: number;
}

type Status = 'ok' | 'warn' | 'unknown';

const Dot: React.FC<{ status: Status }> = ({ status }) => {
  const cls = {
    ok: 'bg-emerald-500',
    warn: 'bg-amber-500',
    unknown: 'bg-muted-foreground/40',
  }[status];
  return <span className={`inline-block w-2.5 h-2.5 rounded-full ${cls}`} />;
};

export const VehicleHistoryV2: React.FC<Props> = ({
  ownerCount, motValidUntil, serviceHistoryComplete, accidentFree, mileageKm,
}) => {
  const lang = useLang();
  const unspecified = { ro: roBuyer, en: enBuyer, de: deBuyer }[lang].unspecified;
  const rows: Array<{ label: string; value: string; status: Status }> = [
    {
      label: 'Istoric accidente',
      value: accidentFree === true ? 'Fără accidente (declarat de vânzător)' : accidentFree === false ? 'Cu evenimente (declarat de vânzător)' : unspecified,
      status: accidentFree === true ? 'ok' : accidentFree === false ? 'warn' : 'unknown',
    },
    {
      label: 'Kilometraj declarat',
      value: mileageKm != null ? (mileageKm === 0 ? '0 km (nou)' : `${mileageKm.toLocaleString('ro-RO')} km`) : unspecified,
      status: mileageKm != null ? 'ok' : 'unknown',
    },
    {
      label: 'Proprietari anteriori',
      value: ownerCount != null ? (ownerCount === 0 ? 'Nou (fără proprietari anteriori)' : `${ownerCount} proprietar${ownerCount > 1 ? 'i' : ''}`) : unspecified,
      status: ownerCount === 0 || ownerCount === 1 ? 'ok' : ownerCount && ownerCount > 1 ? 'warn' : 'unknown',
    },
    {
      label: 'ITP valabil până la',
      value: motValidUntil && !Number.isNaN(Date.parse(motValidUntil)) ? new Date(motValidUntil).toLocaleDateString('ro-RO', { month: 'long', year: 'numeric' }) : unspecified,
      status: motValidUntil ? 'ok' : 'unknown',
    },
    {
      label: 'Carte de service',
      value: serviceHistoryComplete === true ? 'Completă' : serviceHistoryComplete === false ? 'Necompletată' : unspecified,
      status: serviceHistoryComplete ? 'ok' : 'unknown',
    },
  ];

  return (
    <div className="rounded-xl border border-border bg-card p-5">
      <h3 className="font-semibold text-foreground mb-4">Verificare și istoric vehicul</h3>
      <ul className="space-y-3">
        {rows.map((r) => (
          <li key={r.label} className="flex items-center justify-between gap-3 text-sm">
            <span className="flex items-center gap-2.5 text-foreground">
              <Dot status={r.status} />
              {r.label}
            </span>
            <span className={`text-right ${r.status === 'ok' ? 'text-emerald-700 dark:text-emerald-400' : r.status === 'warn' ? 'text-amber-700 dark:text-amber-400' : 'text-muted-foreground'}`}>
              {r.value}
            </span>
          </li>
        ))}
      </ul>
      <a
        href="https://www.rarom.ro/"
        target="_blank"
        rel="noopener noreferrer"
        className="mt-4 inline-flex items-center gap-1.5 text-sm text-primary hover:underline"
      >
        Verifică independent pe RAR.ro <ExternalLink className="h-3.5 w-3.5" />
      </a>
    </div>
  );
};
